import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiProducts from 'api/products';
import BasicTable from 'components/layout/tables/BasicTable';
import IProduct from 'api/interfaces/products/IProductGet';
import SearchInput from 'components/layout/forms/SearchInput';
import {
  columnSearchHeader,
  useColumnSearch,
} from 'services/tables/columnSearch';
import { withContentWidths } from 'services/tables/getColumnWidths';
import useDateLocale from 'services/translations/useDateLocale';

import productColumns from './productColumns';
import translations from '../../../../services/translations/translations';

const ProductsList = (): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation([translations.products, translations.common]);
  const dateLocale = useDateLocale();

  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<IProduct[]>();
  const [selectedRow, setSelectedRow] = useState<number>();
  const [sizePerPage, setSizePerPage] = useState(50);
  const [totalSize, setTotalSize] = useState<number>();

  const selectedProduct =
    products !== undefined && selectedRow !== undefined
      ? products[selectedRow]
      : undefined;
  const [searchTerms, setSearchTerms] = useState<string>('');
  // The searches of the columns, from the first page
  const columnSearch = useColumnSearch(() => {
    setPage(1);
    setOffset(0);
  });

  useEffect(() => {
    if (selectedRow !== undefined) {
      history.push(`/products/${selectedProduct?.id}`);
    }
  }, [history, selectedProduct, selectedRow]);

  useEffect(() => {
    const getProducts = async () => {
      try {
        const list = (await apiProducts.get(
          sizePerPage,
          offset,
          searchTerms,
          undefined,
          false,
          columnSearch.applied
        )) as IProduct[];

        const count = await apiProducts.count(
          searchTerms,
          columnSearch.applied
        );
        setProducts(list);
        setTotalSize(count.count);
      } catch (error) {
        // define action
      }
    };
    getProducts().catch((e) => console.log(e));
  }, [columnSearch.applied, offset, searchTerms, sizePerPage]);

  // Each column with its search below its name
  const columns = productColumns(t, dateLocale.intl).map(
    ({ searchKey, ...column }) => ({
      ...column,
      headerFormatter: columnSearchHeader({
        onChange: (value) => columnSearch.setValue(searchKey, value),
        placeholder:
          searchKey === 'created'
            ? t(`${translations.products}:listColumns.createdSearch`)
            : t(`${translations.products}:Search column`),
        value: columnSearch.search[searchKey] ?? '',
      }),
    })
  );

  const sized = withContentWidths(columns, products || []);

  const handleTableChange = (type: any, { page, sizePerPage }: any) => {
    setPage(page);
    setSizePerPage(sizePerPage);
    setOffset((page - 1) * sizePerPage);
  };

  return (
    <>
      <Row>
        <Col xs={3}>
          <SearchInput setSearchTerms={setSearchTerms} />
        </Col>
      </Row>
      {/* The widths of the columns follow their content */}
      <div
        className="table-scroll"
        style={{ '--table-min-width': sized.minWidth } as React.CSSProperties}
      >
        <BasicTable
          columns={sized.columns}
          data={products}
          page={page}
          sizePerPage={sizePerPage}
          totalSize={totalSize}
          handleTableChange={handleTableChange}
          setSelectedRow={setSelectedRow}
          keyField="id"
        />
      </div>
    </>
  );
};

export default ProductsList;
